import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Plus, X, CheckCircle2, Clock, Percent, Building2, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const LEAD_TIME_KEYS = ["standard", "priority", "flexible"];

const DISCOUNT_TIERS = [
    { min: 5, max: 9, pct: 5 },
    { min: 10, max: 24, pct: 10 },
    { min: 25, max: 49, pct: 15 },
    { min: 50, max: null, pct: 20 },
];

function tierForUnits(n) {
    for (const t of DISCOUNT_TIERS) {
        if (n >= t.min && (t.max === null || n <= t.max)) return t;
    }
    return null;
}

let rowIdCounter = 0;
const nextRowId = () => `row-${rowIdCounter++}`;

export default function BulkQuote() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const { items: cartItems } = useCart();
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState({
        name: "",
        institution: "",
        email: "",
        phone: "",
        country: "",
        leadTime: "standard",
        notes: "",
    });
    const [items, setItems] = useState([{ id: nextRowId(), compound: "", quantity: 10, dose: "" }]);
    const [status, setStatus] = useState({ loading: false, success: false, error: "" });
    const [prefilledFromCart, setPrefilledFromCart] = useState(false);

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    }, []);

    // Prefill contact fields from signed-in user
    useEffect(() => {
        if (user && user.email) {
            setForm((prev) => ({
                ...prev,
                name: prev.name || user.name || "",
                email: prev.email || user.email,
            }));
        }
    }, [user]);

    // Prefill items from cart when ?fromCart=1
    useEffect(() => {
        if (searchParams.get("fromCart") === "1" && cartItems.length > 0 && !prefilledFromCart) {
            setItems(
                cartItems.map((it) => ({
                    id: nextRowId(),
                    compound: it.name,
                    dose: it.dose || "",
                    quantity: Math.max(it.quantity, 5),
                }))
            );
            setPrefilledFromCart(true);
            // Strip the query param so a refresh doesn't re-fire
            const next = new URLSearchParams(searchParams);
            next.delete("fromCart");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, cartItems, prefilledFromCart, setSearchParams]);

    const totalUnits = items.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
    const tier = tierForUnits(totalUnits);

    const updateItem = (id, patch) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    };
    const addItem = () => setItems((prev) => [...prev, { id: nextRowId(), compound: "", quantity: 10, dose: "" }]);
    const removeItem = (id) => setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.id !== id) : prev));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, success: false, error: "" });
        const payload = {
            name: form.name,
            institution: form.institution,
            email: form.email,
            phone: form.phone || null,
            country: form.country,
            lead_time: form.leadTime,
            notes: form.notes || null,
            items: items
                .filter((it) => it.compound && (parseInt(it.quantity, 10) || 0) > 0)
                .map((it) => ({
                    compound: it.compound,
                    quantity: parseInt(it.quantity, 10),
                    dose: it.dose || null,
                })),
        };
        if (payload.items.length === 0) {
            setStatus({ loading: false, success: false, error: t("bulk.errorNoItems") });
            return;
        }
        try {
            await api.post("/bulk-quote", payload);
            setStatus({ loading: false, success: true, error: "" });
            setForm({ name: "", institution: "", email: "", phone: "", country: "", leadTime: "standard", notes: "" });
            setItems([{ id: nextRowId(), compound: "", quantity: 10, dose: "" }]);
        } catch (e) {
            setStatus({
                loading: false,
                success: false,
                error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
            });
        }
    };

    const applyCartToItems = () => {
        if (cartItems.length === 0) return;
        setItems(
            cartItems.map((it) => ({
                id: nextRowId(),
                compound: it.name,
                dose: it.dose || "",
                quantity: Math.max(it.quantity, 5),
            }))
        );
        setPrefilledFromCart(true);
    };

    return (
        <div data-testid="bulk-quote-page" className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-20">
                {/* Left column: intro + tiers + lead time */}
                <div className="lg:col-span-5">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("bulk.eyebrow")}</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tighter">
                        {t("bulk.titleLine1")}
                        <br />
                        <span className="text-[#0077b6]">{t("bulk.titleLine2")}</span>
                    </h1>
                    <p className="mt-6 sm:mt-8 text-neutral-600 leading-relaxed text-sm sm:text-base">{t("bulk.intro")}</p>

                    {/* Volume discount tiers */}
                    <div className="mt-8 sm:mt-10 border border-neutral-200 rounded-xl overflow-hidden" data-testid="discount-tiers">
                        <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2 bg-[#00aeef]/[0.06]">
                            <Percent className="w-4 h-4 text-[#0077b6]" />
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] font-bold">
                                {t("bulk.tiersEyebrow")}
                            </div>
                        </div>
                        <div className="divide-y divide-neutral-200">
                            {DISCOUNT_TIERS.map((tt) => {
                                const isActive = tier && tier.min === tt.min;
                                return (
                                    <div
                                        key={tt.min}
                                        className={`flex items-center justify-between px-5 py-3.5 ${isActive ? "bg-[#00aeef]/[0.08]" : "bg-white"}`}
                                        data-testid={`tier-${tt.min}`}
                                    >
                                        <div className="text-sm text-neutral-800">
                                            {tt.max ? `${tt.min}–${tt.max}` : `${tt.min}+`} {t("bulk.units")}
                                        </div>
                                        <div className={`font-display font-bold text-lg ${isActive ? "text-[#0077b6]" : "text-neutral-900"}`}>
                                            −{tt.pct}%
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Lead times */}
                    <div className="mt-6 border border-neutral-200 rounded-xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2 bg-[#00aeef]/[0.06]">
                            <Clock className="w-4 h-4 text-[#0077b6]" />
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] font-bold">
                                {t("bulk.leadTimesEyebrow")}
                            </div>
                        </div>
                        <div className="divide-y divide-neutral-200 bg-white">
                            {LEAD_TIME_KEYS.map((k) => (
                                <div key={k} className="px-5 py-3.5">
                                    <div className="text-sm font-semibold text-neutral-900">{t(`bulk.leadTimes.${k}.label`)}</div>
                                    <div className="text-xs text-neutral-500 mt-0.5">{t(`bulk.leadTimes.${k}.desc`)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 sm:mt-8 border border-[#00aeef]/30 bg-[#f8f8f8]/5 p-4 sm:p-5 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-4 h-4 text-[#0077b6]" />
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">{t("bulk.institutionalEyebrow")}</div>
                        </div>
                        <div className="text-sm text-neutral-700 leading-relaxed">{t("bulk.institutionalBody")}</div>
                    </div>
                </div>

                {/* Right column: form */}
                <div className="lg:col-span-7">
                    <form onSubmit={submit} className="border border-neutral-200 p-5 sm:p-8 lg:p-10 rounded-xl bg-white">
                        <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6 sm:mb-8">
                            {t("bulk.formTitle")}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                            <Field label={`${t("bulk.name")} *`}>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-name"
                                />
                            </Field>
                            <Field label={`${t("bulk.institution")} *`}>
                                <input
                                    required
                                    value={form.institution}
                                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-institution"
                                />
                            </Field>
                            <Field label={`${t("bulk.email")} *`}>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-email"
                                />
                            </Field>
                            <Field label={t("bulk.phone")}>
                                <input
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-phone"
                                />
                            </Field>
                            <Field label={`${t("bulk.country")} *`}>
                                <input
                                    required
                                    value={form.country}
                                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-country"
                                />
                            </Field>
                            <Field label={`${t("bulk.leadTimeSelect")} *`}>
                                <select
                                    required
                                    value={form.leadTime}
                                    onChange={(e) => setForm({ ...form, leadTime: e.target.value })}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                    data-testid="bulk-lead-time"
                                >
                                    {LEAD_TIME_KEYS.map((k) => (
                                        <option key={k} value={k}>
                                            {t(`bulk.leadTimes.${k}.label`)}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        {/* Compounds list */}
                        <div className="mb-6">
                            {(cartItems.length > 0 || prefilledFromCart) && (
                                <div
                                    className={`mb-4 flex items-center justify-between gap-3 border rounded-lg px-4 py-3 text-xs ${
                                        prefilledFromCart
                                            ? "border-[#00aeef]/40 bg-[#00aeef]/[0.08] text-[#0077b6]"
                                            : "border-neutral-200 bg-neutral-50 text-neutral-700"
                                    }`}
                                    data-testid="cart-prefill-banner"
                                >
                                    <div className="flex items-center gap-2">
                                        <ShoppingCart className="w-4 h-4" />
                                        <span>
                                            {prefilledFromCart
                                                ? t("bulk.prefilledFromCart")
                                                : t("bulk.cartAvailable", { count: cartItems.length })}
                                        </span>
                                    </div>
                                    {!prefilledFromCart && (
                                        <button
                                            type="button"
                                            onClick={applyCartToItems}
                                            className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0077b6] hover:text-[#005f92]"
                                            data-testid="bulk-load-cart"
                                        >
                                            {t("bulk.loadFromCart")}
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="flex items-end justify-between mb-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">{t("bulk.compoundsLabel")}</div>
                                    <div className="text-xs text-neutral-500 mt-1">{t("bulk.compoundsHint")}</div>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                                    {t("bulk.totalUnits")}:{" "}
                                    <span className="text-[#0077b6] font-bold text-sm" data-testid="bulk-total-units">
                                        {totalUnits}
                                    </span>
                                    {tier && (
                                        <span
                                            className="ml-3 inline-flex items-center gap-1 text-[#0077b6] font-bold"
                                            data-testid="bulk-current-discount"
                                        >
                                            −{tier.pct}%
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3">
                                {items.map((it, idx) => (
                                    <div
                                        key={it.id}
                                        className="grid grid-cols-12 gap-2 sm:gap-3 items-start"
                                        data-testid={`bulk-item-${idx}`}
                                    >
                                        <div className="col-span-12 sm:col-span-6">
                                            <input
                                                list="bulk-compound-list"
                                                required
                                                placeholder={t("bulk.compoundPlaceholder")}
                                                value={it.compound}
                                                onChange={(e) => updateItem(it.id, { compound: e.target.value })}
                                                className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                                data-testid={`bulk-item-compound-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-6 sm:col-span-3">
                                            <input
                                                placeholder={t("bulk.dosePlaceholder")}
                                                value={it.dose}
                                                onChange={(e) => updateItem(it.id, { dose: e.target.value })}
                                                className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900"
                                                data-testid={`bulk-item-dose-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-4 sm:col-span-2">
                                            <input
                                                type="number"
                                                min="1"
                                                required
                                                value={it.quantity}
                                                onChange={(e) => updateItem(it.id, { quantity: e.target.value })}
                                                className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900 text-center"
                                                data-testid={`bulk-item-qty-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => removeItem(it.id)}
                                                disabled={items.length === 1}
                                                className="p-3 border border-neutral-200 rounded-lg hover:border-[#0089c8] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                aria-label={t("bulk.removeRow")}
                                                data-testid={`bulk-item-remove-${idx}`}
                                            >
                                                <X className="w-4 h-4 text-neutral-700" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <datalist id="bulk-compound-list">
                                {products.map((p) => (
                                    <option key={p.id} value={p.name} />
                                ))}
                            </datalist>

                            <button
                                type="button"
                                onClick={addItem}
                                className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0077b6] hover:text-[#005f92] transition-colors"
                                data-testid="bulk-add-row"
                            >
                                <Plus className="w-4 h-4" /> {t("bulk.addCompound")}
                            </button>
                        </div>

                        <div className="mb-6">
                            <Field label={t("bulk.notes")}>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={4}
                                    placeholder={t("bulk.notesPlaceholder")}
                                    className="w-full bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-4 py-3 text-sm text-neutral-900 resize-none"
                                    data-testid="bulk-notes"
                                />
                            </Field>
                        </div>

                        {status.error && (
                            <div
                                className="mb-4 border border-red-500/40 bg-red-50 text-red-700 text-xs p-3 rounded-lg"
                                data-testid="bulk-error"
                            >
                                {status.error}
                            </div>
                        )}
                        {status.success && (
                            <div
                                className="mb-4 border border-[#00aeef]/40 bg-[#00aeef]/10 text-neutral-900 text-xs p-3 rounded-lg flex items-center gap-2"
                                data-testid="bulk-success"
                            >
                                <CheckCircle2 className="w-4 h-4 text-[#0077b6]" /> {t("bulk.success")}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status.loading}
                            className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-50 text-white font-bold uppercase tracking-[0.18em] text-xs px-7 py-4 rounded-lg transition-colors"
                            data-testid="bulk-submit"
                        >
                            <Send className="w-4 h-4" />
                            {status.loading ? t("bulk.sending") : t("bulk.send")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 block mb-2">{label}</label>
            {children}
        </div>
    );
}
